import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'

import { useClient } from '@hooks/useClient'
import { usePagination } from '@hooks/usePagination'
import { CampaignTypeEnum } from '@interfaces/promotionCampaign/CampaignTypeEnum'
import { CampaignStatusEnum } from '@interfaces/promotionCampaign/CampaignStatusEnum'
import { CampaignSearchFormValues } from '../../components/CampaignSearchForm/interface'
import { CampaignListPageProps } from './interface'

export function withCampaignListPage(
  Component: React.FC<CampaignListPageProps>
) {
  function WithCampaignListPage() {
    const client = useClient()
    const navigate = useNavigate()
    const [params] = useSearchParams()

    const [searchParams, setSearchParams] = useState<CampaignSearchFormValues>({
      text: params.get('text') || '',
      type: (params.get('type') as CampaignTypeEnum) || undefined,
      status: (params.get('status') as CampaignStatusEnum) || undefined,
    })

    const { page, perpage, pageChange } = usePagination({
      page: Number(params.get('page')) || 1,
    })

    const { data: campaignList, isLoading } = useQuery(
      ['campaign-list', searchParams, page, perpage],
      () =>
        client?.promotionCampaignClient.getCampaignList({
          page,
          limitPerPage: perpage,
          searchText: searchParams.text || undefined,
          type: searchParams.type || undefined,
          status: searchParams.status || undefined,
        })
    )

    function handleSubmitSearch(values: CampaignSearchFormValues) {
      pageChange(1)
      setSearchParams(values)
    }

    function handleResetSearch() {
      pageChange(1)
      setSearchParams({
        text: '',
        type: undefined,
        status: undefined,
      })
    }

    function handlePageChange(newPage: number) {
      pageChange(newPage)
    }

    const componentProps: CampaignListPageProps = {
      data: campaignList?.data || [],
      total: campaignList?.total || 0,
      page,
      perpage,
      isLoading,
      searchParams,
      navigate,
      handlePageChange,
      handleSubmitSearch,
      handleResetSearch,
    }

    return <Component {...componentProps} />
  }

  return WithCampaignListPage
}
